import { useEffect, useState } from 'react'
import { Card, CardContent, Button, Input, Label } from '@/components/ui'
import { Save, RotateCcw, RefreshCw, TrendingUp, MapPin, Home, Wrench, Building2 } from 'lucide-react'
import { fetchCorrezioni, saveCorrezioni, type OmiCorrezione } from '@/services/omi-correzioni.service'

const CATEGORIA_INFO: Record<string, { label: string; icon: typeof TrendingUp; description: string }> = {
  globale: {
    label: 'Aggiornamento Mercato',
    icon: TrendingUp,
    description: 'Fattore globale applicato a tutti i prezzi OMI. Usa questo per adeguare i dati OMI al mercato attuale.',
  },
  provincia: {
    label: 'Correzioni per Provincia',
    icon: MapPin,
    description: 'Aggiustamenti specifici per ogni provincia della Sardegna.',
  },
  caratteristiche: {
    label: 'Caratteristiche Immobile',
    icon: Home,
    description: 'Bonus e malus applicati in base alle caratteristiche dell\'immobile.',
  },
  tipo_immobile: {
    label: 'Tipo Immobile',
    icon: Building2,
    description: 'Moltiplicatori per tipo di proprietà. 1.00 = nessuna variazione.',
  },
  servizi: {
    label: 'Servizi Extra',
    icon: Wrench,
    description: 'Bonus per servizi e pertinenze aggiuntive.',
  },
}

const CATEGORIA_ORDER = ['globale', 'provincia', 'caratteristiche', 'tipo_immobile', 'servizi']

export function OmiCorrezioniPanel() {
  const [correzioni, setCorrezioni] = useState<OmiCorrezione[]>([])
  const [original, setOriginal] = useState<OmiCorrezione[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  const isDirty = JSON.stringify(correzioni) !== JSON.stringify(original)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setIsLoading(true)
    const data = await fetchCorrezioni()
    setCorrezioni(data)
    setOriginal(JSON.parse(JSON.stringify(data)))
    setIsLoading(false)
  }

  function updateValue(key: string, newValue: number) {
    setCorrezioni(prev =>
      prev.map(c => (c.key === key ? { ...c, value: newValue } : c))
    )
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      await saveCorrezioni(correzioni)
      setOriginal(JSON.parse(JSON.stringify(correzioni)))
      setFeedback({ type: 'success', msg: 'Correzioni salvate con successo!' })
    } catch (err) {
      setFeedback({ type: 'error', msg: `Errore: ${err}` })
    }
    setIsSaving(false)
    setTimeout(() => setFeedback(null), 3000)
  }

  function handleReset() {
    setCorrezioni(JSON.parse(JSON.stringify(original)))
  }

  // Raggruppa per categoria
  const grouped = CATEGORIA_ORDER.map(cat => ({
    categoria: cat,
    items: correzioni.filter(c => c.categoria === cat),
  })).filter(g => g.items.length > 0)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Caricamento correzioni...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Correzioni Valutazione OMI</h2>
          <p className="text-sm text-gray-500 mt-1">
            Regola i coefficienti per calibrare le valutazioni rispetto ai dati OMI grezzi.
            I valori sono espressi in percentuale (es. 5 = +5%, -3 = -3%).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={!isDirty || isSaving}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Annulla
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`p-3 rounded-lg text-sm ${
          feedback.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {feedback.msg}
        </div>
      )}

      {/* Categorie */}
      {grouped.map(({ categoria, items }) => {
        const info = CATEGORIA_INFO[categoria]
        if (!info) return null
        const Icon = info.icon
        const isMoltiplicatore = categoria === 'tipo_immobile'

        return (
          <Card key={categoria}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary-50">
                  <Icon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{info.label}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{info.description}</p>
                </div>
              </div>

              <div className="grid gap-3">
                {items.map(item => {
                  const displayValue = isMoltiplicatore
                    ? item.value
                    : Math.round(item.value * 10000) / 100

                  return (
                    <div
                      key={item.key}
                      className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <Label className="text-sm font-medium text-gray-700 truncate block">
                          {item.label}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Input
                          type="number"
                          step={isMoltiplicatore ? '0.05' : '0.5'}
                          className="w-24 text-right text-sm"
                          value={displayValue}
                          onChange={(e) => {
                            const raw = parseFloat(e.target.value) || 0
                            const decimal = isMoltiplicatore ? raw : raw / 100
                            updateValue(item.key, decimal)
                          }}
                        />
                        <span className="text-sm text-gray-500 w-6">
                          {isMoltiplicatore ? 'x' : '%'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )
      })}

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-800 mb-1">Come funziona</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• I dati OMI di base provengono dall'Agenzia delle Entrate (84 comuni della Sardegna)</li>
          <li>• Il <strong>fattore di aggiornamento</strong> si applica a tutti i prezzi (es. +5% se il mercato è salito)</li>
          <li>• Le <strong>correzioni provinciali</strong> aggiustano per area geografica</li>
          <li>• I <strong>bonus/malus</strong> si applicano in base alle caratteristiche specifiche dell'immobile</li>
          <li>• Formula: <code>prezzo_OMI × (1 + aggiornamento + corr_provincia) × (1 + bonus_caratteristiche) × molt_tipo</code></li>
        </ul>
      </div>
    </div>
  )
}
